import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import { EasTableWithFilter } from 'ui/EasTableWithFilter';
import { PAGE_SIZE } from 'services/constants/env';
import queryString from 'query-string';
import { ESortDirection, ETableStructureType, TTableStructure } from 'ui/EasTable/types';
import { TFilters, TOrderBy } from 'redux/types';
import { DATES_ARRAY, DATES_ID } from 'services/constants/dates-constants';
import { tableStructure } from './tableStructure';
import { EMManagementIdHeader } from '../../EMManagementIdHeader';
import { EVatType, TEmVatListItem } from '../../../../../redux/reducers/emVat/types';
import { getEmVatList, getEmVatListLoading } from '../../../../../redux/reducers/emVat/selectors';
import {
	deleteEmVatRequest,
	getEmVatListRequest,
} from '../../../../../redux/reducers/emVat/reducer';
import { EasTableWithoutFilter } from '../../../../../ui/EasTableWithoutFilter';
import { EditVatNumbersDialog } from './EditVatNumbersDialog';
import { ETypeOfDialog } from './EditVatNumbersDialog/constants';
import { ConfirmationDialog } from '../../../../../ui/ConfirmationDialog';

export const VatNumbersManagement = () => {
	const { emId } = useParams();
	const dispatch = useDispatch();
	const { search } = useLocation();
	const navigateParams = queryString.parse(search);
	const vatList = useSelector(getEmVatList);
	const vatListLoading = useSelector(getEmVatListLoading);

	const [currentTableStructure, setCurrentTableStructure] = useState(tableStructure);
	const [openEditVatNumbersDialog, setOpenEditVatNumbersDialog] = useState(false);
	const [AddOrEditDialog, setAddOrEditDialog] = useState<ETypeOfDialog>(ETypeOfDialog.ADD);
	const [idToEdit, setIdToEdit] = useState<number | null>(null);
	const [openDeleteConfirmationDialog, setOpenDeleteConfirmationDialog] = useState(false);
	const setCloseDeleteConfirmationDialog = () => {
		setOpenDeleteConfirmationDialog(false);
	};
	const handleDeleteConfirmationDialog = () => {
		if (!idToEdit) return;
		const vatType = vatList?.data?.find((item) => item.id === idToEdit)?.vatType;
		if (!vatType) return;
		if (vatType !== EVatType.WAREHOUSE && vatType !== EVatType.FIXED_ESTABLISHMENT) return;
		if (!emId) return;
		dispatch(
			deleteEmVatRequest({
				id: idToEdit,
				vatType,
				listRequest: {
					pageNumber: 1,
					pageSize: 100,
					searchString: '',
					filters: [{ fieldName: 'emId', value: emId, operator: '=' }],
					orderBy: [{ fieldName: 'id', direction: ESortDirection.DESC }],
				},
			}),
		);
		setOpenDeleteConfirmationDialog(false);
	};
	const handleTableStructureChange = useCallback(
		(newTableStructure: TTableStructure<keyof TEmVatListItem>[]) => {
			setCurrentTableStructure(newTableStructure);
		},
		[],
	);

	const { page } = queryString.parse(search);

	const filters: TFilters = currentTableStructure.reduce((acc, item) => {
		if (!!navigateParams[item.selector as string] && item.type === ETableStructureType.DATE) {
			if (navigateParams[item.selector as string] === DATES_ID.CUSTOM_RANGE) {
				if (navigateParams[`${String(item.selector)}StartDate`]) {
					acc.push({
						fieldName: String(item.selector),
						value: navigateParams[`${String(item.selector)}StartDate`] as string,
						operator: '>=',
					});
				}
				if (navigateParams[`${String(item.selector)}EndDate`]) {
					acc.push({
						fieldName: String(item.selector),
						value: navigateParams[`${String(item.selector)}EndDate`] as string,
						operator: '<=',
					});
				}
				return acc;
			}
			const dates = DATES_ARRAY.find((i) => i.id === navigateParams[item.selector as string]);
			if (dates?.from)
				acc.push({ fieldName: String(item.selector), value: dates.from, operator: '>=' });
			if (dates?.to)
				acc.push({ fieldName: String(item.selector), value: dates.to, operator: '<=' });
			return acc;
		}
		if (navigateParams[item.selector as string])
			acc.push({
				fieldName: String(item.selector),
				value: navigateParams[item.selector as string] as string,
				operator: '=',
			});
		return acc;
	}, [] as TFilters);

	useEffect(() => {
		const orderBy: TOrderBy[] = [];
		if (navigateParams?.sortIndex) {
			orderBy.push({
				fieldName: currentTableStructure[+navigateParams.sortIndex]?.selector as string,
				direction:
					navigateParams?.sortDirection && String(navigateParams.sortDirection) === 'desc'
						? ESortDirection.DESC
						: ESortDirection.ASC,
			});
		}
		orderBy.push({ fieldName: 'id', direction: ESortDirection.DESC });
		if (!emId) return;

		dispatch(
			getEmVatListRequest({
				pageNumber: 1, // page ? +page : 1,
				pageSize: PAGE_SIZE,
				searchString: String(navigateParams?.search ?? ''),
				filters: [{ fieldName: 'emId', value: emId, operator: '=' }],
				orderBy,
			}),
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch, search]);

	const handleAction = (id: number, typeOfDialog: string) => {
		if (typeOfDialog === 'Edit') {
			setIdToEdit(id);
			setOpenEditVatNumbersDialog(true);
			setAddOrEditDialog(ETypeOfDialog.EDIT);
		} else if (typeOfDialog === 'Delete') {
			setOpenDeleteConfirmationDialog(true);
			setIdToEdit(id);
		}
	};

	return (
		<>
			<EasTableWithoutFilter
				tableStructure={currentTableStructure}
				items={vatList}
				itemsIsLoading={vatListLoading}
				handleAction={handleAction}
				handleLink={() => {}}
				skeletonRows={[1]}
				onTableStructureChange={handleTableStructureChange}
			>
				<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
					<Typography variant="body1" color="primary" fontWeight="bold">
						VAT Numbers Management
					</Typography>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
						{vatListLoading && <CircularProgress size={20} />}
						{/* <Button variant="contained" color="primary" size="small">
							Add History
						</Button> */}
					</Box>
					<Button
						variant="contained"
						color="primary"
						size="small"
						onClick={() => {
							setOpenEditVatNumbersDialog(true);
							setAddOrEditDialog(ETypeOfDialog.ADD);
						}}
					>
						Add VAT Number
					</Button>
				</Box>
			</EasTableWithoutFilter>
			<EditVatNumbersDialog
				open={openEditVatNumbersDialog}
				setClose={() => setOpenEditVatNumbersDialog(false)}
				typeOfDialog={AddOrEditDialog}
				data={idToEdit ? (vatList?.data?.find((item) => item.id === idToEdit) ?? null) : null}
			/>
			<ConfirmationDialog
				open={openDeleteConfirmationDialog}
				setClose={setCloseDeleteConfirmationDialog}
				handleConfirm={handleDeleteConfirmationDialog}
				title="Delete VAT Number"
				text="Are you sure you want to delete this VAT number?"
			/>
		</>
	);
};
